<template>
    <div class="overflow-y-auto">
        <app-list
            ref="list"
            v-model="items"
            class="ltr"
            fill
            :searchable="!isInvestor()"
            :searching="searching"
            @search="onSearch"
        />
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';

import { usePaginatedCollection } from '@composables/usePaginatedCollection';

import OfferWorkflowStatusEnum from '@constants/enums/offer/workflowStatus';

import { Offer, Product, Investor, Adviser } from '@/models';
import AppListItem from '@/models/AppListItem';
import Sidebar from '@/components/sidebars/Sidebar.vue';

const PAGE_SIZE = 50;
const GROUP_IDS = ['offers', 'products', 'investors', 'advisers'];

export default {
    name: 'LibrarySidebar',
    extends: Sidebar,
    setup() {
        const { proxy } = getCurrentInstance();

        const paging = new Map(
            GROUP_IDS.map(id => [
                id,
                usePaginatedCollection({
                    fetchPage: (offset, limit) => proxy.fetchNextGroupPage(id, offset, limit),
                    onPage: rows => proxy.appendGroupRows(id, rows),
                    onError: (error, retry) => proxy.handleError(error, retry),
                    getItems: () => proxy.getGroup(id)?.items || [],
                    limit: PAGE_SIZE
                })
            ])
        );

        return { paging };
    },
    data: () => ({
        items: [],
        searching: false,
        searchQuery: null,
        searchRun: 0
    }),
    async mounted() {
        await this.trySetup();

        await this.$nextTick();

        const activeEl = document.querySelector('.app-list__item--active');

        if (activeEl) {
            activeEl.scrollIntoView({
                behavior: 'smooth'
            });
        }
    },
    methods: {
        async setup() {
            this.items = [
                this.toGroup('offers', 'offers', { library: 'marketing' }, 'offer'),
                this.toGroup('products', 'products', { library: 'marketing' }, 'product', { $hidden: true }),
                this.toGroup('investors', 'investors', { library: 'investor' }, 'investor', {
                    $hidden: this.isFundManagerOrProvider(),
                    before_change_route: () => async () => {
                        this.$store.dispatch('app/removeQuerySelector', 'investor_id');
                    }
                }),
                this.toGroup('advisers', 'advisers', { library: 'adviser' }, 'adviser', {
                    $hidden: !this.isAdviser()
                })
            ];

            this.items = await Promise.all(this.items.map(item => item.setup(true)));
        },
        toGroup(id, icon, params, type, attributes = {}) {
            return new AppListItem({
                id,
                name: this.$t(id),
                icon: this.getIcon(icon),
                route: { name: 'Library', params, query: { type } },
                populate: () => () => this.populateGroup(id),
                on_intersect: () => child => this.paging.get(id).onIntersect(child),
                empty_text: this.$t('no_results_found'),
                ...attributes
            });
        },
        getGroup(id) {
            return this.items.find(item => item.id === id);
        },
        fetchGroupPage(id, offset, limit) {
            const api = this.getApi(id);
            const filters = this.getApiFilters(id);

            return this.searchQuery
                ? api.search(this.searchQuery, { ...filters, offset, limit })
                : api.index(offset, limit, null, filters);
        },
        /**
         * @returns {Promise<object|null>} null when a search started while the page was in flight
         */
        async fetchNextGroupPage(id, offset, limit) {
            const run = this.searchRun;
            const group = this.getGroup(id);

            group.$loading_more = true;

            try {
                const response = await this.fetchGroupPage(id, offset, limit);

                return run === this.searchRun ? response : null;
            } finally {
                group.$loading_more = false;
            }
        },
        appendGroupRows(id, rows) {
            const group = this.getGroup(id);

            if (!group) {
                return;
            }

            const ids = new Set(group.items.map(item => item.id));

            group.items = [
                ...group.items,
                ...rows.filter(row => !ids.has(row.id)).map(row => this.toAppListItem(row, id))
            ];
        },
        /**
         * @returns {Promise<AppListItem[]|null>} null when the request failed or a later search started
         */
        async populateGroup(id) {
            const pager = this.paging.get(id);
            const run = this.searchRun;
            let items = [];

            pager.resetPagination();

            try {
                const response = await this.fetchGroupPage(id, 0, PAGE_SIZE);

                if (run !== this.searchRun) {
                    return null;
                }

                pager.setFirstPage(response);
                items = response.data.map(row => this.toAppListItem(row, id));
            } catch (error) {
                this.handleError(error);
                return null;
            }

            const { id: selectedId, type } = this.$route.query;

            if (!this.searchQuery && selectedId && type === this.getGroup(id)?.route.query.type) {
                if (!items.some(item => item.id === selectedId)) {
                    try {
                        const found = (await this.getApi(id).get(selectedId)).data;

                        if (found) {
                            items.unshift(this.toAppListItem(found, id));
                        }
                    } catch (error) {
                        this.handleError(error);
                    }
                }
            }

            return run === this.searchRun ? items : null;
        },
        getApi(id) {
            switch (id) {
                case 'offers':
                    return Offer.api;
                case 'products':
                    return Product.api;
                case 'investors':
                    return Investor.api;
                case 'advisers':
                    return Adviser.api;
                default:
                    return null;
            }
        },
        getApiFilters(id) {
            const isGI = this.isGrowthInvest();

            switch (id) {
                case 'offers':
                    if (isGI) {
                        return {
                            workflow_status: `${this.getFilterOperator('in')}:[${OfferWorkflowStatusEnum.DRAFT},${
                                OfferWorkflowStatusEnum.LIVE
                            }]`,
                            off_panel: true
                        };
                    } else {
                        return {};
                    }
                default:
                    return {};
            }
        },
        async onSearch(searchQuery) {
            const run = ++this.searchRun;

            this.searching = true;
            this.searchQuery = searchQuery || null;

            try {
                const items = await Promise.all(
                    this.items.map(async item => {
                        if (item.$disabled || item.$readonly || item.$hidden || !this.getApi(item.id)) {
                            return item;
                        }

                        const rows = await this.populateGroup(item.id);

                        if (rows) {
                            item.items = rows;
                            item.$expanded = item.$expanded || rows.length > 0;
                        }

                        return item;
                    })
                );

                this.items = await Promise.all(items.map(item => item.setup(true)));
            } catch (error) {
                this.handleError(error);
            } finally {
                if (run === this.searchRun) {
                    this.searching = false;
                }
            }
        },
        toAppListItem(value, type) {
            if (type === 'offers') {
                return new AppListItem({
                    id: value.id,
                    name: value.name,
                    route: {
                        name: 'Library',
                        params: { library: 'marketing' },
                        query: { type: 'offer', id: value.id }
                    }
                });
            } else if (type === 'products') {
                return new AppListItem({
                    id: value.id,
                    name: value.name,
                    route: {
                        name: 'Library',
                        params: { library: 'marketing' },
                        query: { type: 'product', id: value.id }
                    }
                });
            } else if (type === 'investors') {
                return new AppListItem({
                    id: value.id,
                    name: value.name,
                    description: value.email,
                    tooltip: value.gi_ref,
                    route: {
                        name: 'Library',
                        params: { library: 'investor' },
                        query: { type: 'investor', id: value.id }
                    }
                });
            } else if (type === 'advisers') {
                return new AppListItem({
                    id: value.id,
                    name: value.name,
                    tooltip: value.gi_ref,
                    route: {
                        name: 'Library',
                        params: { library: 'adviser' },
                        query: { type: 'adviser', id: value.id }
                    }
                });
            }
        }
    }
};
</script>
